import { Suspense, lazy, useMemo, type ComponentType } from 'react';
import { PageSkeleton, EmptyState } from '@deloitte-us-consulting/gp-sdk';
import type { PluginEntry } from './registry';
import { loadRemoteModule } from './loadRemote';

const FEDERATION = import.meta.env.MODE === 'federation';

/**
 * Mount a federated remote's `./Module` at runtime from its registry `remoteUrl`.
 * Federation loading only works in the `--mode federation` build (run `pnpm smoke`);
 * in dev/plain builds we show a hint instead of calling the no-op stub loader.
 */
export function RemoteMount({ app }: { app: PluginEntry }) {
  const Remote = useMemo<ComponentType | null>(() => {
    if (!app.remoteUrl || !FEDERATION) return null;
    const url = app.remoteUrl;
    const id = app.appId;
    return lazy(async () => ({ default: await loadRemoteModule(id, url) }));
  }, [app.remoteUrl, app.appId]);

  if (!app.remoteUrl) {
    return <EmptyState title={`${app.displayName} is headless`} description="No UI to render." />;
  }
  if (!FEDERATION) {
    return (
      <EmptyState
        title={`${app.displayName} loads in the federation build`}
        description="Run `pnpm smoke` to build + serve the shell with remotes. `pnpm dev` serves the shell only."
      />
    );
  }
  return (
    <Suspense fallback={<PageSkeleton label={`Loading ${app.displayName}…`} />}>
      {Remote ? <Remote /> : null}
    </Suspense>
  );
}
